import { create } from "zustand";
import { tokenStore } from "@/shared/api/tokenStore";
import { fetchMe } from "@/features/auth/api";
import type { AuthTokens, Operator } from "@/features/auth/types";

interface AuthState {
  operator: Operator | null;
  isAuthed: boolean;
  // True while a hard-refresh bootstrap (token in storage -> fetch me) is in flight.
  isBootstrapping: boolean;

  // Persist tokens + verify support rights via /support/me/. Throws on 403
  // (caller shows "no permission" and we stay logged out).
  login: (tokens: AuthTokens) => Promise<Operator>;
  logout: () => void;
  // On app boot: if tokens exist in storage, re-validate them and restore the operator.
  bootstrap: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  operator: null,
  isAuthed: false,
  isBootstrapping: tokenStore.hasTokens(),

  login: async (tokens) => {
    tokenStore.setTokens(tokens.access, tokens.refresh);
    try {
      const operator = await fetchMe();
      set({ operator, isAuthed: true, isBootstrapping: false });
      return operator;
    } catch (err) {
      // 403 (not staff) or any failure → roll back the tokens we just stored.
      tokenStore.clear();
      set({ operator: null, isAuthed: false, isBootstrapping: false });
      throw err;
    }
  },

  logout: () => {
    tokenStore.clear();
    set({ operator: null, isAuthed: false, isBootstrapping: false });
  },

  bootstrap: async () => {
    if (!tokenStore.hasTokens()) {
      set({ isBootstrapping: false });
      return;
    }
    try {
      const operator = await fetchMe();
      set({ operator, isAuthed: true, isBootstrapping: false });
    } catch {
      tokenStore.clear();
      set({ operator: null, isAuthed: false, isBootstrapping: false });
    }
  },
}));

// Let the axios interceptor force a logout when refresh fails mid-session.
tokenStore.registerLogout(() => {
  useAuthStore.setState({ operator: null, isAuthed: false, isBootstrapping: false });
});
